import {
	AfterViewInit,
	Component,
	ContentChild,
	EventEmitter,
	forwardRef,
	Input,
	OnInit,
	Output,
	TemplateRef,
	ViewChild
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Unsubscriber } from '@core-lib/classes';
import { AutocompleteTemplateType, ControlFieldIdAffaireEnum } from '@core-lib/enums';
import { AutocompleteControlData, IAutocompleteData } from '@core-lib/models/';
import { SharedStoreActions, SharedStoreSelectors } from '@core-lib/store/shared-store';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
@Component({
    selector: 'app-autocomplete',
    templateUrl: './app-autocomplete.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AppAutocompleteComponent),
            multi: true
        }
    ],
    standalone: false
})
export class AppAutocompleteComponent extends Unsubscriber implements OnInit, AfterViewInit {
	@ViewChild('ngSelect') public ngSelect: NgSelectComponent;
	@Input() multiple: boolean;
	@Input() closeOnSelect: boolean;
	@Input() searchable: boolean;
	@Input() addTag: boolean = false;
	@Input() searchService: (term: string) => Observable<IAutocompleteData[]>;
	@Input() controlField: AbstractControl;
	@Input() controlId: string;
	@Input() items?: IAutocompleteData[];
	@Input() formControlName: string;
	@Input() debounceTime: number = 200;
	@Input() isDisabled: boolean = false;
	@Input() minTermLength: number = 2;
	@Input() compareWith: boolean;
	@Input() attributeId: string;
	@Input() clearable: boolean = true;
	@Input() elementsIdsToDisable?: string[];

	@Output() propageAddedDataEvent = new EventEmitter();
	@Output() ngModelChangeEvent = new EventEmitter();

	autocompleteItems$: Subject<string> = new Subject<string>();
	@Input() isCheckboxType: boolean = false;

	@Input() set defaultItems(value: IAutocompleteData[]) {
		if (value) {
			this.autocompleteDataForApiSearch(value);
			// Refresh dropdown
			this.autocompleteItems$.next('');
		}
	}

	liveSearchItems$: Observable<IAutocompleteData[]>;
	value: any;
	filteredValue: IAutocompleteData[] = [];

	dataLoading: boolean = false;

	@ContentChild(TemplateRef) customTemplate: TemplateRef<any>;

	autocompleteTemplateType = AutocompleteTemplateType;
	templateType: AutocompleteTemplateType;

	addTagFc(name: string): any {
		return { label: name, value: name };
	}

	private propagateChange = (_: any) => {};

	constructor(private store$: Store) {
		super();
	}

	ngAfterViewInit(): void {
		if (this.compareWith) {
			this.ngSelect.compareWith = this.compareWithFc;
		}

		if (this.customTemplate) {
			this.templateType = AutocompleteTemplateType.Custom;
		}
	}

	ngOnInit(): void {
		this.setTemplateType();
		if (!this.items) {
			this.autocompleteDataForApiSearch();
		} else {
			this.autocompleteForInternalSearch();
		}

		// New data to add manually (e.g., after user adds item)
		this.store$.select(SharedStoreSelectors.selectAutocompleteDataToAdd).pipe(
			filter(data => !!data && data.controlId === this.controlId),
			map((updateAuto: AutocompleteControlData) => {
				if (this.multiple) {
					this.filteredValue = updateAuto.replaceAll
						? this.autocompleteValue(updateAuto)
						: this.autocompleteValue(updateAuto, this.filteredValue);
					this.writeValue(this.filteredValue, true);
					return [];
				} else {
					// label/value returned
					const value: IAutocompleteData = {
						label: updateAuto.data[0].label,
						value: updateAuto.data[0].value
					};
					this.writeValue(value);
					return [];
				}
			}),
			startWith([]),
			takeUntil(this.destroySubscriptions$)
		).subscribe();

		this.store$.select(SharedStoreSelectors.selectAutocompleteControlToReset).pipe(
			filter(data => !!data && data.includes(this.controlId)),
			map(() => {
                this.filteredValue = [];
                return[];
            }),
			startWith([]),
			takeUntil(this.destroySubscriptions$)
		).subscribe();
	}

	private autocompleteDataForApiSearch(defaultItems: IAutocompleteData[] = []): void {
		// Search term stream
		const searchTerm$ = this.autocompleteItems$.pipe(
			debounceTime(this.debounceTime),
			distinctUntilChanged(),
			startWith(''),
			takeUntil(this.destroySubscriptions$)
		);
		// Added tiers from store
		const addedTiers$ = this.store$
			.select(SharedStoreSelectors.selectAutocompleteAddedTiers)
			.pipe(startWith([]), takeUntil(this.destroySubscriptions$));

		// Search results from API and added in the store elements
		const searchResults$: Observable<IAutocompleteData[]> = combineLatest([searchTerm$, addedTiers$]).pipe(
			// tap(() => (this.dataLoading = true)),
			switchMap(([term, addedTiers]) => {
				const trimmedTerm = term?.trim().toLowerCase();

				// If the search term is empty, return empty list
				if (!trimmedTerm) {
					return of([]);
				}

				this.dataLoading = true;

				// Filter local (added) tiers by label
				let filteredAddedTiers = addedTiers.filter(tier => tier.label.toLowerCase().includes(trimmedTerm));

				//si la recherche est lancée depuis affaire la detection de doublon de tiers mis en cache doit etre impossible 
				//=> disable les tiers qui sont deja utilisé pour partie 1 ou 2 et qui sont dans la cache
				if(this.elementsIdsToDisable != null && 
					(this.controlId == ControlFieldIdAffaireEnum.AffairePartie1ControlId || this.controlId == ControlFieldIdAffaireEnum.AffairePartie2ControlId)){
					filteredAddedTiers = filteredAddedTiers.map(x=> ({ ...x, disabled : this.elementsIdsToDisable?.includes(x.value?.tempId)}))
				}

				// API search
				return this.searchService(term).pipe(
					catchError(() => of([])),
					map(apiResults => {
						// Merge and deduplicate by value
						const merged = [...filteredAddedTiers, ...apiResults].filter(
							(item, index, self) => index === self.findIndex(t => t.value === item.value)
						);
						return merged;
					}),
					tap(() => (this.dataLoading = false))
				);
			}),

			startWith([]),
			takeUntil(this.destroySubscriptions$)
		);

		// Control reset
		const controlToReset$ = this.store$.select(SharedStoreSelectors.selectAutocompleteControlToReset).pipe(
			filter(data => !!data && data.includes(this.controlId)),
			map(() => []),
			startWith([]),
			takeUntil(this.destroySubscriptions$)
		);

		// Static default items
		const defaultItems$ = of([...defaultItems]);

		// Final combined list
		this.liveSearchItems$ = combineLatest([searchResults$, searchTerm$, controlToReset$, defaultItems$]).pipe(
			map(([searchResults, term, resetData, defaults]) => {
				// const termLower = term ? term.toLowerCase().trim() : null;

				// const filteredAdded = termLower ? addedTiers.filter(item => item.label.toLowerCase().includes(termLower)) : addedTiers;

				const combined = [...searchResults, ...resetData, ...defaults];

				// Optional deduplication
				const deduped = combined.filter((item, index, self) => index === self.findIndex(i => i.value === item.value));

				return deduped;
			}),
			takeUntil(this.destroySubscriptions$)
		);
	}



	private autocompleteForInternalSearch(): void {
		const updateItems$ = this.store$.select(SharedStoreSelectors.selectAutocompleteDataToAdd).pipe(
			filter(data => !!data && data.controlId === this.controlId),
			map((updateAuto: AutocompleteControlData) => {
				const newData = updateAuto.data
					.map(data => ({
						label: data.label,
						value: data.value
					}))
					.filter((elem, index, self) => index === self?.indexOf(elem));
				return newData;
			}),
			takeUntil(this.destroySubscriptions$)
		);
		this.liveSearchItems$ = merge(
			of(this.items).pipe(
				map(res => res),
				takeUntil(this.destroySubscriptions$)
			),
			updateItems$,
			this.autocompleteItems$.pipe(
				map(() => this.items),
				takeUntil(this.destroySubscriptions$)
			)
		);
	}

	writeValue(data: any, addAction: boolean = false): void {
		if (addAction) {
			this.value = data;
			this.propagateChange(data?.map(autocompleteData => autocompleteData?.value));
			this.propageAddedDataEvent?.emit(this.controlId);
		} else {
			this.value = data;
			this.propagateChange(this.value);
		}
	}

	compareWithFc(item: any, selected: any): boolean {
		if (!this?.items) {
			return item.value !== selected.id;
		}
	}

	onChange($event: any): void {
		this.filteredValue = $event;
	}

	// The function registerOnChange is part of Angular's ControlValueAccessor interface
	// It registers a function to be called when value changes
	registerOnChange(fn: (date: Date) => void): void {
		this.propagateChange = fn;
	}

	// Registers a function to be called when control is touched
	registerOnTouched(fn: () => void): void {}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	public handleNgModelChange($event: any): void {
		this.writeValue($event);
		this.ngModelChangeEvent.emit($event);
	}

	private setTemplateType(): void {
		if (this.isCheckboxType) {
			this.templateType = AutocompleteTemplateType.Checkbox;
		} else {
			this.templateType = AutocompleteTemplateType.Defaut;
		}
	}

	private autocompleteValue(newData: AutocompleteControlData, currentData?: IAutocompleteData[]): IAutocompleteData[] {
		let finalValue: IAutocompleteData[];
		if (currentData) {
			finalValue = [...currentData, ...newData.data].filter((elem, index, self) => index === self?.indexOf(elem));
		} else {
			finalValue = newData.data;
		}
		return finalValue;
	}

	public setDefaultItems(defaultItems: IAutocompleteData[]): void {
		if (defaultItems) {
			this.autocompleteDataForApiSearch(defaultItems);
			// Refresh dropdown
			this.autocompleteItems$.next('');
		}
	}

	onClear() {
		this.store$.dispatch(
			SharedStoreActions.ResetAutocompleteData({
				param: [this.controlId]
			})
		);
	}
}
